import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Phone } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { useContact } from "../../context/ContentContext";
import { submitQuote } from "../../lib/api";

export default function ContactCta({ defaultService = "" }) {
  const { toast } = useToast();
  const CONTACT = useContact();
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || defaultService || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: initialService, message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const s = searchParams.get("service") || defaultService;
    if (s) setForm((f) => ({ ...f, service: s }));
  }, [searchParams, defaultService]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing info", description: "Please add your name and email." });
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast({ title: "Request sent!", description: "Our team will contact you within 24 hours." });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-20 lg:py-28 bg-homi-black overflow-hidden">
      <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-homi-gold/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center relative">
        <div>
          <span className="homi-section-tag text-homi-gold text-sm">Get In Touch</span>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white mt-3 mb-5 leading-tight">Ready to Start Your Next Project?</h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-lg">Request your free, no-obligation quote today. Our friendly team is ready to help build or repair your home.</p>
          <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-4 group">
            <span className="w-14 h-14 rounded-full bg-homi-gold flex items-center justify-center group-hover:scale-105 transition-transform">
              <Phone className="w-6 h-6 text-homi-black" />
            </span>
            <span>
              <span className="block text-sm text-white/60">Call us anytime</span>
              <span className="block font-heading font-bold text-xl text-white">{CONTACT.phone}</span>
            </span>
          </a>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl p-8 shadow-2xl">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <input value={form.name} onChange={update("name")} placeholder="Full Name" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-homi-gold transition-colors" />
            <input value={form.email} onChange={update("email")} type="email" placeholder="Email Address" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-homi-gold transition-colors" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <input value={form.phone} onChange={update("phone")} placeholder="Phone Number" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-homi-gold transition-colors" />
            <input value={form.service} onChange={update("service")} placeholder="Service Needed" className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-homi-gold transition-colors" />
          </div>
          <textarea value={form.message} onChange={update("message")} rows={4} placeholder="Tell us about your project..." className="w-full border border-gray-200 rounded-lg px-4 py-3 mb-5 focus:outline-none focus:border-homi-gold transition-colors resize-none" />
          <button type="submit" disabled={loading} className="homi-btn-shine w-full inline-flex items-center justify-center gap-2 bg-homi-gold hover:bg-homi-goldDark text-homi-black font-heading font-bold px-6 py-4 rounded-lg transition-colors disabled:opacity-70">
            {loading ? "Sending..." : <>Get My Free Quote <Send className="w-5 h-5" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}
